import { UserService } from './user.service';

import { inject, Injectable } from '@angular/core';

import { verifyPhoneNumber } from '../constants/constants';
import moment from 'moment';
import { isNil } from 'src/utils/utils';
import { UntypedFormGroup } from '@angular/forms';

export interface SmsPauseConfig {
  isPause: boolean;
  start: number;
  end: number;
}

export interface SummerWarningSmsPauseConfig {
  smsPause: SmsPauseConfig;
}

export interface UpdateDeliveryProductOptions {
  deliveryData: any;
  formGroupDelivery: UntypedFormGroup;
  deliveryFormControlCfg: Record<string, any>;
  productList: any;
  egwgmaSmsPause: SmsPauseConfig;
  egwsummerConfig: SummerWarningSmsPauseConfig;
  isGmaAlarmProduct: (productId: any) => boolean;
  isSummerWarningProduct: (productId: any) => boolean;
}

export interface SelectEachProductOptions {
  section: string;
  duringImport?: boolean;
  productList: any;
  deliveryTargets: { email: string[]; sms: string[]; fax: string[] };
  deliveryFormControlCfg: Record<string, any>;
  formGroupDelivery: UntypedFormGroup;
  defaultDateFrom: any;
}

export interface UserStationChangeOptions {
  section: string;
  product: any;
  checked: boolean;
  defaultDateFrom: any;
}

export interface UserProductChangeOptions {
  section: string;
  product: any;
  checked: boolean;
  defaultDateFrom: any;
}

export interface DeliveryProductChangeOptions {
  checked: boolean;
  product: any;
  formControlName: string;
  start: string;
  end: string;
  egwgmaSmsPause: SmsPauseConfig;
  egwsummerConfig: SummerWarningSmsPauseConfig;
}

@Injectable({
  providedIn: 'root',
})
export class UserAdminUserProductService {
  private readonly userService = inject(UserService);

  productInUser(
    productId: number,
    userProducts: any[],
    options: any = {}
  ): any {
    for (const product of userProducts ?? []) {
      if (product.id !== productId) {
        continue;
      }

      if (
        options.hasOwnProperty('parameterset') &&
        product.hasOwnProperty('parameterset')
      ) {
        if (
          product.parameterset === options.parameterset &&
          options.resolution?.days === product.resolution?.days &&
          options.resolution?.hourly === product.resolution?.hourly
        ) {
          return product;
        }

        continue;
      }

      return product;
    }

    return null;
  }

  productIndex(productId: number, productList: any[]): number {
    return (productList ?? []).findIndex(product => product.id === productId);
  }

  isStationInUserProduct(
    productId: number,
    stationId: string,
    userProducts: any[]
  ): boolean {
    const product = (userProducts ?? []).find(item => item.id === productId);

    if (!product?.station) {
      return false;
    }

    return product.station.includes(stationId);
  }

  newUserProduct(
    productName: string,
    section: string,
    productId: number,
    firstStationId: string = null,
    defaultDateFrom: any,
    duringImport = false,
    deliveryTargets: { email: string[]; sms: string[]; fax: string[] } = {
      email: [],
      sms: [],
      fax: [],
    },
    options: any = {}
  ): object {
    const product = {
      id: productId,
      active: true,
      start: defaultDateFrom,
      end: this.userService.getEndlessDtg(),
    };

    if (
      productName === 'eop' ||
      productName === 'smw' ||
      productName === 'epp'
    ) {
      product['station'] = [firstStationId];
    }

    if (productName === 'eop') {
      product['parameter'] = null;
    }

    if (productName === 'epp' && section === 'delivery') {
      product['parameterset'] = options.parameterset;
      const [days, hourly] = options.res.split('_');
      product['resolution'] = { days: Number(days), hourly: Number(hourly) };
    }

    if (duringImport && section === 'delivery') {
      product['email'] = [...deliveryTargets.email];

      if (deliveryTargets.sms.length > 0) {
        product['sms'] = [...deliveryTargets.sms];
      }

      if (deliveryTargets.fax.length > 0) {
        product['fax'] = [...deliveryTargets.fax];
      }
    }

    return product;
  }

  getDefaultDateFrom(
    section: string,
    sectionFormGroups: {
      website: any;
      delivery: any;
      app: any;
      masterData: any;
    },
    duringImport = false
  ): any {
    if (duringImport) {
      return moment().format('YYYY-MM-DD') + 'T00:00:00Z';
    }

    const sectionFormGroup =
      section === 'online'
        ? sectionFormGroups.website
        : section === 'delivery'
          ? sectionFormGroups.delivery
          : sectionFormGroups.app;

    return sectionFormGroup.controls.dateFrom.value !== null
      ? sectionFormGroup.controls.dateFrom.value
      : sectionFormGroups.masterData.controls.dateFrom.value;
  }

  userProductDeliveryValues(userData: any, product: any): string[] {
    const deliveryProducts = userData?.metadata?.delivery?.product;

    if (!Array.isArray(deliveryProducts)) {
      return [];
    }

    const values: string[] = [];

    for (const deliveryProduct of deliveryProducts) {
      if (!this.isMatchingDeliveryProduct(product, deliveryProduct)) {
        continue;
      }

      for (const targetType of ['email', 'fax', 'sms']) {
        const addresses = deliveryProduct[targetType];

        if (!Array.isArray(addresses)) {
          continue;
        }

        for (const address of addresses) {
          if (targetType === 'email') {
            values.push(targetType + address);
            continue;
          }

          const verifiedAddress = verifyPhoneNumber(address);

          if (verifiedAddress !== null) {
            values.push(targetType + verifiedAddress);
          }
        }
      }
    }

    return values;
  }

  userProductDeliveryValueBySection(
    userData: any,
    product: any,
    section: string,
    isThreshold = true
  ): any {
    const deliveryProducts = userData?.metadata?.delivery?.product;

    if (isNil(deliveryProducts)) {
      return undefined;
    }

    const currentDeliveryProduct = deliveryProducts.find(
      deliveryProduct => deliveryProduct.id == product.id
    );

    if (isNil(currentDeliveryProduct)) {
      return undefined;
    }

    if (!isThreshold) {
      return currentDeliveryProduct[section];
    }

    return (currentDeliveryProduct.thresholds ?? []).find(
      threshold => threshold.parameter === section
    )?.level;
  }

  private isMatchingDeliveryProduct(
    product: any,
    deliveryProduct: any
  ): boolean {
    if (product.id !== deliveryProduct.id) {
      return false;
    }

    if (product.hasOwnProperty('resolution')) {
      return (
        deliveryProduct.station?.includes(product.station.stationId) &&
        deliveryProduct.parameterset === product.parameterset &&
        deliveryProduct.resolution?.days === product.resolution?.days
      );
    }

    if (product.hasOwnProperty('station')) {
      return deliveryProduct.station?.includes(product.station.stationId);
    }

    return true;
  }

  updateDeliveryProduct(options: UpdateDeliveryProductOptions): any[] {
    const {
      deliveryData,
      formGroupDelivery,
      deliveryFormControlCfg,
      productList,
      egwgmaSmsPause,
      egwsummerConfig,
      isGmaAlarmProduct,
      isSummerWarningProduct,
    } = options;

    const userProducts = deliveryData.product ?? [];
    const updatedData = [];

    for (const formControlName in deliveryFormControlCfg) {
      const formControl = formGroupDelivery.controls[formControlName];
      const config = deliveryFormControlCfg[formControlName];

      const productId = config.productId;
      const stationId = config.stationId ?? null;
      const parameterset = config.parameterset ?? null;
      const resolution = config.resolution ?? null;

      for (const userProduct of userProducts) {
        let addressCount = 0;

        if (
          this.isMatchingProductConfig(
            userProduct,
            productId,
            stationId,
            parameterset,
            resolution
          ) &&
          userProduct.active &&
          formControl.valid &&
          formControl.value.length > 0
        ) {
          addressCount = this.patchDeliveryAddresses(
            userProduct,
            formControl.value,
            stationId,
            deliveryData
          );
        }

        if (addressCount > 0) {
          this.patchSmsPauseConfig(
            userProduct,
            productList,
            config,
            egwgmaSmsPause,
            egwsummerConfig,
            isGmaAlarmProduct,
            isSummerWarningProduct,
            formGroupDelivery
          );

          updatedData.push(userProduct);
        }
      }
    }

    return updatedData;
  }

  private isMatchingProductConfig(
    userProduct: any,
    productId: any,
    stationId: any,
    parameterset: any,
    resolution: any
  ): boolean {
    if (userProduct.id !== productId) {
      return false;
    }

    const stationMatches =
      stationId === null || userProduct.station?.indexOf(stationId) >= 0;

    const parametersetMatches =
      parameterset === null || userProduct.parameterset === parameterset;

    const resolutionMatches =
      resolution === null || userProduct.resolution?.days === resolution.days;

    return stationMatches && parametersetMatches && resolutionMatches;
  }

  private patchDeliveryAddresses(
    userProduct: any,
    formControlValues: string[],
    stationId: any,
    deliveryData: any
  ): number {
    let addressCount = 0;

    for (const deliveryType of ['email', 'sms', 'fax']) {
      const addresses = [];

      for (const value of formControlValues) {
        if (!value.startsWith(deliveryType)) {
          continue;
        }

        const address = value.substring(deliveryType.length);

        if (!addresses.includes(address)) {
          addresses.push(address);
          addressCount++;
        }
      }

      userProduct[deliveryType] = addresses;

      if (stationId !== null) {
        userProduct.station = [stationId];
      }

      userProduct.start = deliveryData.start;
      userProduct.end = deliveryData.end;
    }

    return addressCount;
  }

  private patchSmsPauseConfig(
    userProduct: any,
    productList: any,
    config: any,
    egwgmaSmsPause: SmsPauseConfig,
    egwsummerConfig: SummerWarningSmsPauseConfig,
    isGmaAlarmProduct: (productId: any) => boolean,
    isSummerWarningProduct: (productId: any) => boolean,
    formGroupDelivery: UntypedFormGroup
  ): void {
    if (isGmaAlarmProduct(userProduct.id)) {
      userProduct.pauseStart = egwgmaSmsPause.isPause
        ? egwgmaSmsPause.start
        : 0;
      userProduct.pauseEnd = egwgmaSmsPause.isPause ? egwgmaSmsPause.end : 0;

      return;
    }

    if (!isSummerWarningProduct(userProduct.id)) {
      return;
    }

    userProduct.pauseStart = egwsummerConfig.smsPause.isPause
      ? egwsummerConfig.smsPause.start
      : 0;
    userProduct.pauseEnd = egwsummerConfig.smsPause.isPause
      ? egwsummerConfig.smsPause.end
      : 0;

    this.patchSummerWarningConfig(
      userProduct,
      productList,
      config,
      formGroupDelivery
    );
  }

  private patchSummerWarningConfig(
    userProduct: any,
    productList: any,
    config: any,
    formGroupDelivery: UntypedFormGroup
  ): void {
    const currentEgwsummerProduct = productList.egwsummer.find(
      product => product.id == userProduct.id
    );

    userProduct.thresholds = [];

    if (isNil(currentEgwsummerProduct)) {
      return;
    }

    if (currentEgwsummerProduct.productData.info) {
      const infoFormControl = formGroupDelivery.controls[config.info];
      userProduct.info = infoFormControl.value;
    } else {
      userProduct.info = null;
    }

    this.addSummerWarningThresholdIfAvailable(
      userProduct,
      currentEgwsummerProduct,
      formGroupDelivery,
      config.windWarning,
      'windWarning'
    );

    this.addSummerWarningThresholdIfAvailable(
      userProduct,
      currentEgwsummerProduct,
      formGroupDelivery,
      config.nowcast,
      'nowcast'
    );

    this.addSummerWarningThresholdIfAvailable(
      userProduct,
      currentEgwsummerProduct,
      formGroupDelivery,
      config.heavyRain,
      'heavyRainWarning'
    );

    this.addSummerWarningThresholdIfAvailable(
      userProduct,
      currentEgwsummerProduct,
      formGroupDelivery,
      config.precipitationWarning,
      'precipitationWarning'
    );
  }

  private addSummerWarningThresholdIfAvailable(
    userProduct: any,
    currentEgwsummerProduct: any,
    formGroupDelivery: UntypedFormGroup,
    formControlName: string,
    parameter: string
  ): void {
    if (!currentEgwsummerProduct.productData[parameter]) {
      return;
    }

    const formControl = formGroupDelivery.controls[formControlName];

    userProduct.thresholds.push({
      parameter,
      level: formControl.value,
    });
  }

  selectEachProduct(options: SelectEachProductOptions): void {
    const {
      section,
      duringImport = false,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
    } = options;

    this.ensureSectionProductList(section);

    const addresses = this.createDeliveryAddressValues(
      section,
      deliveryTargets
    );

    this.selectStationProducts({
      section,
      duringImport,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
      addresses,
    });

    this.selectEppProducts({
      section,
      duringImport,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
      addresses,
    });

    this.selectOtherProducts({
      section,
      duringImport,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
      addresses,
    });
  }

  private ensureSectionProductList(section: string): void {
    const product =
      this.userService.userData()?.['metadata']?.[section]?.['product'];

    if (product !== null) {
      return;
    }

    this.userService.userData.update(userData => ({
      ...userData,
      metadata: {
        ...userData['metadata'],
        [section]: {
          ...userData['metadata'][section],
          product: [],
        },
      },
    }));
  }

  private createDeliveryAddressValues(
    section: string,
    deliveryTargets: { email: string[]; sms: string[]; fax: string[] }
  ): string[] {
    if (section !== 'delivery') {
      return [];
    }

    const addresses: string[] = [];

    for (const targetType of Object.keys(deliveryTargets)) {
      for (const address of deliveryTargets[targetType]) {
        addresses.push(targetType + address);
      }
    }

    return addresses;
  }

  private selectStationProducts(
    options: SelectEachProductOptions & { addresses: string[] }
  ): void {
    const {
      section,
      duringImport = false,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
      addresses,
    } = options;

    const stationProducts = section === 'delivery' ? [] : ['smw'];

    for (const productName of stationProducts) {
      for (const productType in productList[productName]) {
        for (const product of productList[productName][productType]) {
          const stationId = product.station.stationId;

          this.selectStationProduct({
            productName,
            section,
            product,
            stationId,
            duringImport,
            deliveryTargets,
            defaultDateFrom,
          });

          if (section === 'delivery' && !duringImport) {
            this.patchDeliveryFormControlsForStationProduct(
              deliveryFormControlCfg,
              formGroupDelivery,
              product.id,
              stationId,
              addresses.filter(address => !address.startsWith('sms'))
            );
          }
        }
      }
    }
  }

  private selectStationProduct(options: {
    productName: string;
    section: string;
    product: any;
    stationId: string;
    duringImport: boolean;
    deliveryTargets: { email: string[]; sms: string[]; fax: string[] };
    defaultDateFrom: any;
  }): void {
    const {
      productName,
      section,
      product,
      stationId,
      duringImport,
      deliveryTargets,
      defaultDateFrom,
    } = options;

    let userProduct = this.productInUser(
      product.id,
      this.userService.userData()['metadata'][section]['product']
    );

    if (userProduct === null) {
      userProduct = this.newUserProduct(
        productName,
        section,
        product.id,
        stationId,
        defaultDateFrom,
        duringImport,
        deliveryTargets
      );

      this.pushUserProduct(section, userProduct);
      return;
    }

    const productIndex = this.productIndex(
      product.id,
      this.userService.userData()['metadata'][section]['product']
    );

    if (!userProduct.active) {
      this.patchUserProduct(section, productIndex, { active: true });
    }

    if (
      !this.isStationInUserProduct(
        product.id,
        stationId,
        this.userService.userData()['metadata'][section]['product']
      )
    ) {
      this.addStationToUserProduct(section, productIndex, stationId);
    }
  }

  private selectEppProducts(
    options: SelectEachProductOptions & { addresses: string[] }
  ): void {
    const {
      section,
      duringImport = false,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
      addresses,
    } = options;

    if (section !== 'delivery') {
      const parameterset = Object.keys(productList.epp)[0];
      const res = Object.keys(productList.epp[parameterset])[0];

      for (const product of productList.epp[parameterset][res]) {
        this.selectEppProduct({
          section,
          product,
          parameterset: null,
          res: null,
          duringImport,
          deliveryTargets,
          defaultDateFrom,
        });
      }

      return;
    }

    for (const parameterset of Object.keys(productList.epp)) {
      for (const res of Object.keys(productList.epp[parameterset])) {
        for (const product of productList.epp[parameterset][res]) {
          this.selectEppProduct({
            section,
            product,
            parameterset,
            res,
            duringImport,
            deliveryTargets,
            defaultDateFrom,
          });

          if (!duringImport) {
            this.patchDeliveryFormControlsForStationProduct(
              deliveryFormControlCfg,
              formGroupDelivery,
              product.id,
              product.station.stationId,
              addresses.filter(address => !address.startsWith('sms'))
            );
          }
        }
      }
    }
  }

  private selectEppProduct(options: {
    section: string;
    product: any;
    parameterset: string | null;
    res: string | null;
    duringImport: boolean;
    deliveryTargets: { email: string[]; sms: string[]; fax: string[] };
    defaultDateFrom: any;
  }): void {
    const {
      section,
      product,
      parameterset,
      res,
      duringImport,
      deliveryTargets,
      defaultDateFrom,
    } = options;

    const stationId = product.station.stationId;
    const productOptions =
      section === 'delivery' && parameterset && res
        ? {
            parameterset,
            resolution: {
              days: Number(res.split('_')[0]),
              hourly: Number(res.split('_')[1]),
            },
          }
        : {};

    let userProduct = this.productInUser(
      product.id,
      this.userService.userData()['metadata'][section]['product'],
      productOptions
    );

    if (userProduct === null || !userProduct.hasOwnProperty('station')) {
      userProduct = this.newUserProduct(
        'epp',
        section,
        product.id,
        stationId,
        defaultDateFrom,
        duringImport,
        deliveryTargets,
        section === 'delivery' ? { parameterset, res } : {}
      );

      this.pushUserProduct(section, userProduct);
      return;
    }

    const productIndex = this.productIndex(
      product.id,
      this.userService.userData()['metadata'][section]['product']
    );

    if (!userProduct.active) {
      this.patchUserProduct(section, productIndex, { active: true });
    }

    if (
      !this.isStationInUserProduct(
        product.id,
        stationId,
        this.userService.userData()['metadata'][section]['product']
      )
    ) {
      this.addStationToUserProduct(section, productIndex, stationId);
    }
  }

  private selectOtherProducts(
    options: SelectEachProductOptions & { addresses: string[] }
  ): void {
    const {
      section,
      duringImport = false,
      productList,
      deliveryTargets,
      deliveryFormControlCfg,
      formGroupDelivery,
      defaultDateFrom,
      addresses,
    } = options;

    const otherProducts =
      section === 'delivery'
        ? ['egp', 'egpsummer', 'egw', 'egwsummer', 'marwis']
        : [
            'egp',
            'egpsummer',
            'egw',
            'egwsummer',
            'radar',
            'marwis',
            'ampel',
            'tauben',
            'trend',
            'route',
          ];

    for (const productName of otherProducts) {
      for (const product of productList[productName]) {
        this.selectSimpleProduct({
          productName,
          section,
          product,
          duringImport,
          deliveryTargets,
          defaultDateFrom,
        });

        if (section === 'delivery') {
          const filteredAddresses =
            productName === 'egp' || productName === 'egpsummer'
              ? addresses.filter(address => !address.startsWith('sms'))
              : addresses;

          this.patchDeliveryFormControlsForProduct(
            deliveryFormControlCfg,
            formGroupDelivery,
            product.id,
            filteredAddresses
          );
        }
      }
    }
  }

  private selectSimpleProduct(options: {
    productName: string;
    section: string;
    product: any;
    duringImport: boolean;
    deliveryTargets: { email: string[]; sms: string[]; fax: string[] };
    defaultDateFrom: any;
  }): void {
    const {
      productName,
      section,
      product,
      duringImport,
      deliveryTargets,
      defaultDateFrom,
    } = options;

    let userProduct = this.productInUser(
      product.id,
      this.userService.userData()['metadata'][section]['product']
    );

    if (userProduct === null) {
      userProduct = this.newUserProduct(
        productName,
        section,
        product.id,
        null,
        defaultDateFrom,
        duringImport,
        deliveryTargets
      );

      this.pushUserProduct(section, userProduct);
      return;
    }

    const productIndex = this.productIndex(
      product.id,
      this.userService.userData()['metadata'][section]['product']
    );

    this.patchUserProduct(section, productIndex, { active: true });
  }

  private pushUserProduct(section: string, userProduct: any): void {
    this.userService.userData.update(userData => ({
      ...userData,
      metadata: {
        ...userData['metadata'],
        [section]: {
          ...userData['metadata'][section],
          product: [...userData['metadata'][section]['product'], userProduct],
        },
      },
    }));
  }

  private patchUserProduct(
    section: string,
    productIndex: number,
    partialProduct: any
  ): void {
    this.userService.userData.update(userData => {
      const updatedProduct = structuredClone(
        userData['metadata'][section]['product']
      );

      updatedProduct[productIndex] = {
        ...updatedProduct[productIndex],
        ...partialProduct,
      };

      return {
        ...userData,
        metadata: {
          ...userData['metadata'],
          [section]: {
            ...userData['metadata'][section],
            product: updatedProduct,
          },
        },
      };
    });
  }

  private addStationToUserProduct(
    section: string,
    productIndex: number,
    stationId: string
  ): void {
    this.userService.userData.update(userData => {
      const updatedProduct = structuredClone(
        userData['metadata'][section]['product']
      );

      updatedProduct[productIndex].station = [
        ...(updatedProduct[productIndex].station ?? []),
        stationId,
      ];

      updatedProduct[productIndex].active = true;

      return {
        ...userData,
        metadata: {
          ...userData['metadata'],
          [section]: {
            ...userData['metadata'][section],
            product: updatedProduct,
          },
        },
      };
    });
  }

  private patchDeliveryFormControlsForStationProduct(
    deliveryFormControlCfg: Record<string, any>,
    formGroupDelivery: UntypedFormGroup,
    productId: any,
    stationId: any,
    addresses: string[]
  ): void {
    for (const formControlName in deliveryFormControlCfg) {
      const config = deliveryFormControlCfg[formControlName];

      if (config.productId === productId && config.stationId === stationId) {
        formGroupDelivery.controls[formControlName].setValue(addresses);
      }
    }
  }

  private patchDeliveryFormControlsForProduct(
    deliveryFormControlCfg: Record<string, any>,
    formGroupDelivery: UntypedFormGroup,
    productId: any,
    addresses: string[]
  ): void {
    for (const formControlName in deliveryFormControlCfg) {
      const config = deliveryFormControlCfg[formControlName];

      if (config.productId === productId) {
        formGroupDelivery.controls[formControlName].setValue(addresses);
      }
    }
  }

  initSectionProducts(section: string, selectAll = false): void {
    const userData = this.userService.userData();

    if (!userData.hasOwnProperty('metadata') || userData.metadata === null) {
      this.userService.userData.update(currentUserData => ({
        ...currentUserData,
        metadata: {},
      }));
    }

    if (
      !this.userService.userData().metadata.hasOwnProperty(section) ||
      this.userService.userData().metadata[section] === null
    ) {
      this.userService.userData.update((currentUserData: any) => ({
        ...currentUserData,
        metadata: {
          ...currentUserData.metadata,
          [section]: {},
        },
      }));
    }

    if (
      !this.userService.userData().metadata[section].hasOwnProperty('product')
    ) {
      this.userService.userData.update((currentUserData: any) => ({
        ...currentUserData,
        metadata: {
          ...currentUserData.metadata,
          [section]: {
            ...currentUserData.metadata[section],
            product: selectAll ? null : [],
          },
        },
      }));
    }
  }

  changeUserStationProduct(options: UserStationChangeOptions): void {
    const { section, product, checked, defaultDateFrom } = options;
    const stationId = product.station.stationId;

    this.initSectionProducts(section);

    let productIndex = this.productIndex(
      product.id,
      this.userService.userData().metadata[section].product
    );

    if (productIndex === -1) {
      const newProduct = {
        id: product.id,
        active: true,
        start: defaultDateFrom,
        end: this.userService.getEndlessDtg(),
        parameter: null,
        station: [],
      };

      this.pushUserProduct(section, newProduct);

      productIndex =
        this.userService.userData().metadata[section].product.length - 1;
    }

    if (checked) {
      this.addStationToUserProduct(section, productIndex, stationId);
      return;
    }

    this.removeStationFromUserProduct(section, productIndex, stationId);
  }

  private removeStationFromUserProduct(
    section: string,
    productIndex: number,
    stationId: string
  ): void {
    this.userService.userData.update((userData: any) => {
      const updatedProduct = structuredClone(
        userData.metadata[section].product
      );

      updatedProduct[productIndex].station = (
        updatedProduct[productIndex].station ?? []
      ).filter((currentStationId: string) => currentStationId !== stationId);

      updatedProduct[productIndex].active =
        updatedProduct[productIndex].station.length > 0;

      return {
        ...userData,
        metadata: {
          ...userData.metadata,
          [section]: {
            ...userData.metadata[section],
            product: updatedProduct,
          },
        },
      };
    });
  }

  changeUserProduct(options: UserProductChangeOptions): void {
    const { section, product, checked, defaultDateFrom } = options;

    this.initSectionProducts(section);

    let productIndex = this.productIndex(
      product.id,
      this.userService.userData().metadata[section].product
    );

    if (productIndex === -1) {
      const newProduct = {
        id: product.id,
        active: true,
        start: defaultDateFrom,
        end: this.userService.getEndlessDtg(),
      };

      this.pushUserProduct(section, newProduct);

      productIndex =
        this.userService.userData().metadata[section].product.length - 1;
    }

    this.patchUserProduct(section, productIndex, {
      active: checked,
    });
  }

  changeDeliveryProduct(options: DeliveryProductChangeOptions): void {
    const {
      checked,
      product,
      formControlName,
      start,
      end,
      egwgmaSmsPause,
      egwsummerConfig,
    } = options;

    const deliveryIndex = this.getDeliveryProductIndex(product);

    if (deliveryIndex === null && checked) {
      const deliveryProduct = this.createDeliveryProduct(
        product,
        formControlName,
        start,
        end,
        egwgmaSmsPause,
        egwsummerConfig
      );

      this.userService.userData.update((userData: any) => {
        const updatedProduct = structuredClone(
          userData.metadata.delivery.product
        );

        updatedProduct.push(deliveryProduct);

        return {
          ...userData,
          metadata: {
            ...userData.metadata,
            delivery: {
              ...userData.metadata.delivery,
              product: updatedProduct,
            },
          },
        };
      });

      return;
    }

    if (deliveryIndex === null) {
      return;
    }

    this.userService.userData.update((userData: any) => {
      const updatedProduct = structuredClone(
        userData.metadata.delivery.product
      );

      updatedProduct[deliveryIndex].active = checked;

      return {
        ...userData,
        metadata: {
          ...userData.metadata,
          delivery: {
            ...userData.metadata.delivery,
            product: updatedProduct,
          },
        },
      };
    });
  }

  getDeliveryProductIndex(product: any): number | null {
    const deliveryProducts =
      this.userService.userData()?.metadata?.delivery?.product ?? [];

    for (let index = 0; index < deliveryProducts.length; index++) {
      const deliveryProduct = deliveryProducts[index];

      if (deliveryProduct.id !== product.id) {
        continue;
      }

      if (!deliveryProduct.hasOwnProperty('station')) {
        const station = deliveryProduct.station?.[0] ?? null;

        if (station === (product.station ?? null)) {
          return index;
        }

        continue;
      }

      if (product.hasOwnProperty('parameterset')) {
        if (
          deliveryProduct.station?.includes(product.station.stationId) &&
          deliveryProduct.parameterset === product.parameterset &&
          deliveryProduct.resolution?.days === product.resolution?.days
        ) {
          return index;
        }

        continue;
      }

      if (
        product.hasOwnProperty('station') &&
        deliveryProduct.station?.includes(product.station.stationId)
      ) {
        return index;
      }
    }

    return null;
  }

  private createDeliveryProduct(
    product: any,
    formControlName: string,
    start: string,
    end: string,
    egwgmaSmsPause: SmsPauseConfig,
    egwsummerConfig: SummerWarningSmsPauseConfig
  ): any {
    const deliveryProduct: any = {
      id: product.id,
      active: true,
      start,
      end,
    };

    if (product.hasOwnProperty('parameterset')) {
      deliveryProduct.station = [product.station.stationId];
      deliveryProduct.parameterset = product.parameterset;
      deliveryProduct.resolution = product.resolution;
    } else if (product.hasOwnProperty('station')) {
      deliveryProduct.station = [product.station.stationId];
      deliveryProduct.parameter = null;
    }

    if (formControlName.includes('ADDRESSESxEGWGMAx')) {
      deliveryProduct.pauseStart = egwgmaSmsPause.isPause
        ? egwgmaSmsPause.start
        : 0;
      deliveryProduct.pauseEnd = egwgmaSmsPause.isPause
        ? egwgmaSmsPause.end
        : 0;
    }

    if (formControlName.includes('ADDRESSESxEGWSUMMERx')) {
      deliveryProduct.pauseStart = egwsummerConfig.smsPause.isPause
        ? egwsummerConfig.smsPause.start
        : 0;
      deliveryProduct.pauseEnd = egwsummerConfig.smsPause.isPause
        ? egwsummerConfig.smsPause.end
        : 0;
    }

    return deliveryProduct;
  }
}
